import React from 'react';
import PropTypes from 'prop-types';
import { ChevronDown, ChevronUp, X } from 'lucide-react';
import clsx from 'clsx';
import { useTheme } from '../context/ThemeContext';
import { formatPrice } from '../utils/quantities';
import { formatShipDate } from '../utils/dates';

// Import SVG icons
import IconLocationFill from '../images/icon-location-fill.svg';
import ArrowUpRight from '../images/arrow-up-right.svg';
import ArrowDownRight from '../images/arrow-down-right.svg';

const CollapsibleLegHeader = ({
  legIndex,
  isExpanded,
  onToggleExpanded,
  onRemove,
  showRemoveButton = true,
  totalPrice = 0,
  fromAddress,
  toAddress,
  shipDate,
  deliveryDate,
  className = ''
}) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  // Format addresses - just city names
  const originCity = fromAddress?.city || fromAddress?.cruisePort?.city || 'Origin';
  const destinationCity = toAddress?.city || toAddress?.cruisePort?.city || 'Destination';
  const routeDisplay = `${originCity} to ${destinationCity}`;

  // Format dates - MMM D, YYYY (e.g., "Mar 25, 2026")
  const shipDateDisplay = formatShipDate(shipDate, 'MMM D, YYYY');
  const deliveryDateDisplay = formatShipDate(deliveryDate, 'MMM D, YYYY');
    
  const headerClassName = clsx(
    'flex items-start gap-2.5 self-stretch cursor-pointer select-none',
    'px-4 py-4 md:px-10 md:py-6', // Smaller padding on mobile
    {
      'rounded-t-xl': true,
      'bg-slate-800': isDark,
      'bg-[#F5F5F5]': !isDark,
      'rounded-b-none': isExpanded,
      'rounded-xl': !isExpanded
    },
    className
  );

  const legTitleClassName = clsx(
    'text-sm font-semibold',
    {
      'text-white': isDark,
      'text-gray-900': !isDark
    }
  );

  const valueClassName = clsx(
    'text-sm',
    {
      'text-slate-300': isDark,
      'text-gray-600': !isDark
    }
  );

  const priceClassName = clsx(
    'text-sm font-semibold',
    {
      'text-white': isDark,
      'text-gray-900': !isDark
    }
  );

  const expandIconClassName = clsx(
    'transition-colors',
    {
      'text-slate-400 hover:text-white': isDark,
      'text-gray-400 hover:text-gray-600': !isDark
    }
  );

  const removeButtonClassName = clsx(
    'transition-colors p-1 hover:bg-red-500 hover:bg-opacity-20 rounded',
    {
      'text-slate-400 hover:text-red-400': isDark,
      'text-gray-400 hover:text-red-500': !isDark
    }
  );

  const handleRemoveClick = (e) => {
    e.stopPropagation();
    onRemove();
  };

  return (
    <div className={headerClassName} onClick={onToggleExpanded}>
      <div className="flex items-center justify-between w-full">
        <div className="flex items-center gap-6 flex-1">
          {/* Leg title - always visible */}
          <span className={legTitleClassName}>Leg {legIndex + 1}</span>
          
          {/* Route: City to City - hidden on mobile */}
          <div className="hidden md:flex items-center gap-1">
            <img src={IconLocationFill} alt="" className="w-4 h-4 opacity-50" />
            <span className={valueClassName}>{routeDisplay}</span>
          </div>

          {/* Ship date - hidden on mobile */}
          <div className="hidden md:flex items-center gap-1">
            <img src={ArrowUpRight} alt="" className="w-4 h-4 opacity-50" />
            <span className={valueClassName}>{shipDateDisplay}</span>
          </div>

          {/* Delivery date - hidden on mobile */}
          <div className="hidden md:flex items-center gap-1">
            <img src={ArrowDownRight} alt="" className="w-4 h-4 opacity-50" />
            <span className={valueClassName}>{deliveryDateDisplay}</span>
          </div>
        </div>
        
        {/* Right side - price and actions */}
        <div className="flex items-center gap-3">
          {/* Price - hidden on mobile */}
          {totalPrice > 0 && (
            <span className={clsx(priceClassName, 'hidden md:inline')}>{formatPrice(totalPrice)}</span>
          )}
          {/* Remove button - hidden on mobile */}
          {showRemoveButton && (
            <button 
              onClick={handleRemoveClick}
              className={clsx(removeButtonClassName, 'hidden md:block')}
              aria-label="Remove leg"
            >
              <X size={16} />
            </button>
          )}
          {isExpanded ? (
            <ChevronUp size={18} className={expandIconClassName} />
          ) : (
            <ChevronDown size={18} className={expandIconClassName} />
          )}
        </div>
      </div>
    </div>
  );
};

CollapsibleLegHeader.propTypes = {
  legIndex: PropTypes.number.isRequired,
  isExpanded: PropTypes.bool.isRequired,
  onToggleExpanded: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  showRemoveButton: PropTypes.bool,
  totalPrice: PropTypes.number,
  fromAddress: PropTypes.object,
  toAddress: PropTypes.object,
  shipDate: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Date)
  ]),
  deliveryDate: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Date)
  ]),
  className: PropTypes.string
};

export default CollapsibleLegHeader;